import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { usePoints } from "@/hooks/usePoints";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { guideCategories, startGuideSteps, safeGuideTips, bettingGuideTopics, pointExchangeRates } from "@/mocks/guideData";

const tabLabel: Record<string, string> = {
  start: "시작하기",
  safe: "안전가이드",
  betting: "배팅가이드",
  verification: "검증가이드",
  gift: "기프티콘교환",
  point: "포인트교환",
};

export default function GuideCategoryPage() {
  const { tab } = useParams<{ tab: string }>();
  const { user } = useAuth();
  const { points, todayEarned, thisMonthEarned, thisMonthSpent, totalEarned, transactions, loading, error, exchangeGift } = usePoints();
  const currentLabel = tab ? (tabLabel[tab] || "시작하기") : "시작하기";
  const isPointOrGift = tab === "gift" || tab === "point";

  // Gift exchange state
  const [exchangeModal, setExchangeModal] = useState<{ open: boolean; itemName: string; cost: number } | null>(null);
  const [exchangeResult, setExchangeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [exchanging, setExchanging] = useState(false);
  const [filterCat, setFilterCat] = useState<string>("전체");

  // Gift items from Supabase
  const [giftItems, setGiftItems] = useState<Array<{
    id: string;
    name: string;
    description: string;
    points_required: number;
    stock: number;
    image_url: string | null;
    category: string;
    is_active: boolean;
  }>>([]);
  const [giftLoading, setGiftLoading] = useState(false);

  useEffect(() => {
    if (tab === "gift") {
      setGiftLoading(true);
      supabase
        .from("gift_items")
        .select("*")
        .eq("is_active", true)
        .order("points_required", { ascending: true })
        .then(({ data, error: fetchError }) => {
          if (!fetchError && data) {
            setGiftItems(data);
          }
          setGiftLoading(false);
        });
    }
  }, [tab]);

  const filteredGifts = filterCat === "전체" ? giftItems : giftItems.filter((g) => g.category === filterCat);
  const giftCategories = ["전체", ...Array.from(new Set(giftItems.map((g) => g.category)))];

  const handleExchange = async () => {
    if (!exchangeModal) return;
    setExchanging(true);
    const result = await exchangeGift(exchangeModal.itemName, exchangeModal.cost);
    setExchangeResult(result);
    setExchanging(false);
  };

  const closeModal = () => {
    setExchangeModal(null);
    setExchangeResult(null);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[1000px] mx-auto px-3 md:px-4 py-8 md:py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center text-primary-500">
              <i className={tab === "gift" ? "ri-gift-line text-2xl" : tab === "point" ? "ri-coins-line text-2xl" : "ri-book-open-line text-2xl"} />
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-700">{currentLabel}</h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                {tab === "gift" ? "보유 포인트로 다양한 기프티콘으로 교환하세요" : tab === "point" ? "포인트 적립 및 교환 내역을 확인하세요" : `월드컵티비 시즌2 이용가이드 - ${currentLabel}`}
              </p>
            </div>
          </div>
        </div>
      </section>

      {!isPointOrGift && (
        /* Category Tabs */
        <section className="max-w-[1000px] mx-auto px-3 md:px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {guideCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/guide/${cat.id}`}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-colors ${
                  tab === cat.id
                    ? "border-primary-500/50 bg-primary-500/5"
                    : "border-background-300 bg-background-100 hover:border-primary-500/20"
                }`}
              >
                <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center`}>
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-50 text-lg">
                    <i className={cat.icon} />
                  </span>
                </div>
                <span className="text-sm font-bold text-foreground-700">{cat.title}</span>
                <span className="text-[11px] text-foreground-600 text-center">{cat.desc}</span>
              </Link>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-background-100 rounded-lg border border-background-300 p-5 md:p-6">
            {/* 시작하기 */}
            {tab === "start" && (
              <div>
                <h2 className="text-lg font-bold text-foreground-700 mb-6">시작하기</h2>
                <div className="space-y-4">
                  {startGuideSteps.map((step) => (
                    <div key={step.step} className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-black text-foreground-50">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-foreground-700">{step.title}</h3>
                        <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 안전가이드 */}
            {tab === "safe" && (
              <div>
                <h2 className="text-lg font-bold text-foreground-700 mb-6">안전가이드</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {safeGuideTips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 bg-background-50 rounded-lg border border-background-300/50 p-4">
                      <div className="w-9 h-9 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                        <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className={tip.icon} />
                        </span>
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-foreground-700">{tip.title}</h3>
                        <p className="text-xs text-foreground-600 mt-1 leading-relaxed">{tip.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 배팅가이드 */}
            {tab === "betting" && (
              <div>
                <h2 className="text-lg font-bold text-foreground-700 mb-6">배팅가이드</h2>
                <div className="space-y-4">
                  {bettingGuideTopics.map((topic, i) => (
                    <div key={i} className="bg-background-50 rounded-lg border border-background-300/50 p-4">
                      <h3 className="text-base font-bold text-foreground-700 mb-2">{topic.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{topic.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 검증가이드 */}
            {tab === "verification" && (
              <div>
                <h2 className="text-lg font-bold text-foreground-700 mb-4">검증가이드</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  월드컵티비 시즌2의 먹튀검증 시스템은 총 11가지 핵심 지표를 기준으로 사이트를 평가합니다.
                  각 검증 결과는 투명하게 공개되며, 회원님들께서 직접 검증을 요청하실 수도 있습니다.
                </p>
                <div className="bg-background-50 rounded-lg border border-background-300/50 p-5">
                  <h3 className="text-sm font-bold text-foreground-700 mb-3">검증 결과 보는 법</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <div className="flex items-start gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-accent-500 flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill" />
                      </span>
                      <span><strong className="text-foreground-700">인증 완료</strong> — 모든 검증 단계를 통과한 안전한 사이트입니다. 보증금이 예치되어 있어 안심하고 이용하실 수 있습니다.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-secondary-400 flex-shrink-0 mt-0.5">
                        <i className="ri-time-line" />
                      </span>
                      <span><strong className="text-foreground-700">검증 진행 중</strong> — 현재 검증이 진행 중인 사이트입니다. 결과가 나올 때까지 이용을 보류하시는 것을 권장합니다.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-red-400 flex-shrink-0 mt-0.5">
                        <i className="ri-close-circle-fill" />
                      </span>
                      <span><strong className="text-foreground-700">먹튀 확정</strong> — 피해 사례가 확인된 사이트입니다. 절대 이용하지 마시고, 유사 사이트에도 주의하세요.</span>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-6">
                  <Link
                    to="/verification"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    먹튀검증 페이지로 이동
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 기프티콘교환 */}
      {tab === "gift" && (
        <section className="max-w-[1000px] mx-auto px-3 md:px-4 py-6">
          {!user ? (
            <div className="bg-background-100 rounded-lg border border-background-300 p-10 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center text-primary-500">
                <i className="ri-gift-line text-3xl" />
              </span>
              <h3 className="text-base font-bold text-foreground-700 mt-3">로그인이 필요합니다</h3>
              <p className="text-sm text-foreground-600 mt-1 mb-4">기프티콘 교환은 로그인 후 이용할 수 있습니다</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                로그인하러 가기
              </Link>
            </div>
          ) : (
            <>
              {/* 내 포인트 */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 bg-background-100 rounded-lg border border-background-300 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-coins-line" />
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-600">보유 포인트</p>
                    {loading ? (
                      <p className="text-xl font-black text-foreground-400 animate-pulse">불러오는 중...</p>
                    ) : (
                      <p className="text-xl font-black text-primary-500">{points.toLocaleString()} P</p>
                    )}
                  </div>
                </div>
                <Link
                  to="/guide/point"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-secondary-500 text-foreground-50 text-sm font-bold rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-history-line" />
                  </span>
                  포인트 내역 보기
                </Link>
              </div>

              {/* 카테고리 필터 */}
              <div className="flex flex-wrap gap-2 mb-5">
                {giftCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setFilterCat(cat)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      filterCat === cat
                        ? "bg-primary-500 text-foreground-50"
                        : "bg-background-100 text-foreground-600 border border-background-300 hover:border-primary-500/30"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* 오류 표시 */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-red-400">
                    <i className="ri-error-warning-line" />
                  </span>
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* 기프티콘 그리드 */}
              {giftLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-background-100 rounded-lg border border-background-300 overflow-hidden animate-pulse">
                      <div className="w-full h-40 bg-background-200" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-background-300 rounded w-3/4" />
                        <div className="flex items-center justify-between">
                          <div className="h-4 bg-background-300 rounded w-16" />
                          <div className="h-3 bg-background-300 rounded w-12" />
                        </div>
                        <div className="h-8 bg-background-300 rounded w-full mt-3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredGifts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-3">
                    <i className="ri-gift-line text-2xl text-foreground-600" />
                  </div>
                  <p className="text-sm text-foreground-600">해당 카테고리에 등록된 상품이 없습니다</p>
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredGifts.map((item) => (
                  <div key={item.id} className="bg-background-100 rounded-lg border border-background-300 overflow-hidden hover:border-primary-500/20 transition-colors group">
                    <div className="relative w-full h-40 overflow-hidden bg-background-200">
                      <img src={item.image_url || ""} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold bg-background-50/90 text-foreground-700 rounded whitespace-nowrap">{item.category}</span>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-foreground-700 mb-1">{item.name}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-black text-primary-500">{item.points_required.toLocaleString()} P</span>
                        <span className="text-[11px] text-foreground-600">재고 {item.stock}개</span>
                      </div>
                      <button
                        onClick={() => setExchangeModal({ open: true, itemName: item.name, cost: item.points_required })}
                        disabled={loading || points < item.points_required || item.stock <= 0}
                        className={`mt-3 w-full py-2 text-xs font-bold rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                          loading || points < item.points_required || item.stock <= 0
                            ? "bg-background-300 text-foreground-600 cursor-not-allowed"
                            : "bg-primary-500 text-foreground-50 hover:bg-primary-600"
                        }`}
                      >
                        {loading ? "로딩 중..." : item.stock <= 0 ? "품절" : points < item.points_required ? "포인트 부족" : "교환하기"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </>
          )}

          {/* 교환 확인 모달 */}
          {exchangeModal?.open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
              <div className="relative bg-background-100 rounded-lg border border-background-300 shadow-lg p-6 w-full max-w-sm mx-3 z-10">
                {exchangeResult ? (
                  <div className="text-center">
                    <span className={`w-12 h-12 mx-auto flex items-center justify-center rounded-full ${exchangeResult.success ? "bg-primary-500/10 text-primary-500" : "bg-red-500/10 text-red-400"}`}>
                      <i className={`text-2xl ${exchangeResult.success ? "ri-check-line" : "ri-close-line"}`} />
                    </span>
                    <h3 className="text-base font-bold text-foreground-700 mt-3">
                      {exchangeResult.success ? "교환 완료" : "교환 실패"}
                    </h3>
                    <p className="text-sm text-foreground-600 mt-1">{exchangeResult.message}</p>
                    <button
                      onClick={closeModal}
                      className="mt-4 px-5 py-2 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      확인
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-base font-bold text-foreground-700 mb-2">기프티콘 교환</h3>
                    <p className="text-sm text-foreground-600 mb-4">
                      <strong>{exchangeModal.itemName}</strong>을(를) <strong className="text-primary-500">{exchangeModal.cost.toLocaleString()} P</strong>로 교환하시겠습니까?
                    </p>
                    {points < exchangeModal.cost && (
                      <p className="text-xs text-red-400 mb-3">보유 포인트가 부족합니다 (보유: {points.toLocaleString()} P)</p>
                    )}
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={closeModal}
                        className="px-4 py-2 text-sm text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        취소
                      </button>
                      <button
                        onClick={handleExchange}
                        disabled={exchanging || points < exchangeModal.cost}
                        className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                          exchanging || points < exchangeModal.cost
                            ? "bg-background-300 text-foreground-600 cursor-not-allowed"
                            : "bg-primary-500 text-foreground-50 hover:bg-primary-600"
                        }`}
                      >
                        {exchanging ? "처리 중..." : "교환하기"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* 포인트교환 */}
      {tab === "point" && (
        <section className="max-w-[1000px] mx-auto px-3 md:px-4 py-6">
          {!user ? (
            <div className="bg-background-100 rounded-lg border border-background-300 p-10 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center text-primary-500">
                <i className="ri-coins-line text-3xl" />
              </span>
              <h3 className="text-base font-bold text-foreground-700 mt-3">로그인이 필요합니다</h3>
              <p className="text-sm text-foreground-600 mt-1 mb-4">포인트 내역은 로그인 후 확인할 수 있습니다</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                로그인하러 가기
              </Link>
            </div>
          ) : (
            <>
              {/* 내 포인트 요약 */}
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-background-100 rounded-lg border border-background-300 p-4 text-center animate-pulse">
                      <div className="h-3 bg-background-300 rounded w-16 mx-auto mb-2" />
                      <div className="h-6 bg-background-300 rounded w-20 mx-auto mb-1" />
                      <div className="h-2 bg-background-300 rounded w-12 mx-auto" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
                    <p className="text-xs text-foreground-600 mb-1">보유 포인트</p>
                    <p className="text-xl font-black text-primary-500">{points.toLocaleString()}</p>
                    <p className="text-[10px] text-foreground-600 mt-0.5">활동포인트</p>
                  </div>
                  <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
                    <p className="text-xs text-foreground-600 mb-1">이번 달 적립</p>
                    <p className="text-xl font-black text-accent-500">+{thisMonthEarned.toLocaleString()}</p>
                    <p className="text-[10px] text-foreground-600 mt-0.5">2026년 7월</p>
                  </div>
                  <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
                    <p className="text-xs text-foreground-600 mb-1">이번 달 사용</p>
                    <p className="text-xl font-black text-secondary-500">-{thisMonthSpent.toLocaleString()}</p>
                    <p className="text-[10px] text-foreground-600 mt-0.5">기프티콘 교환</p>
                  </div>
                  <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
                    <p className="text-xs text-foreground-600 mb-1">누적 적립</p>
                    <p className="text-xl font-black text-foreground-700">{totalEarned.toLocaleString()}</p>
                    <p className="text-[10px] text-foreground-600 mt-0.5">전체 기간</p>
                  </div>
                </div>
              )}

              {/* 교환 비율 안내 */}
              <div className="bg-background-100 rounded-lg border border-background-300 p-5 md:p-6 mb-6">
                <h2 className="text-lg font-bold text-foreground-700 mb-4">포인트 적립 & 교환 가이드</h2>
                <div className="space-y-4">
                  {pointExchangeRates.map((rate) => (
                    <div key={rate.id} className="flex items-start gap-3 bg-background-50 rounded-lg border border-background-300/50 p-4">
                      <div className="w-9 h-9 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                          <i className="ri-information-line" />
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-bold text-foreground-700">{rate.name}</h3>
                        <p className="text-xs text-primary-500 font-bold mt-0.5">{rate.rate}</p>
                        <p className="text-xs text-foreground-600 mt-1 leading-relaxed">{rate.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 포인트 내역 */}
              <div className="bg-background-100 rounded-lg border border-background-300 p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-foreground-700">포인트 내역</h2>
                  <Link
                    to="/guide/gift"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-foreground-50 text-sm font-bold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-gift-line" />
                    </span>
                    기프티콘 교환하기
                  </Link>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4 flex items-center gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-red-400">
                      <i className="ri-error-warning-line" />
                    </span>
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-4 animate-pulse py-2">
                        <div className="h-4 bg-background-300 rounded w-20" />
                        <div className="h-5 bg-background-300 rounded w-12" />
                        <div className="h-4 bg-background-300 rounded flex-1" />
                        <div className="h-4 bg-background-300 rounded w-16" />
                      </div>
                    ))}
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="w-10 h-10 mx-auto flex items-center justify-center text-foreground-400">
                      <i className="ri-inbox-line text-2xl" />
                    </span>
                    <p className="text-sm text-foreground-600 mt-2">아직 포인트 내역이 없습니다</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-300">
                          <th className="text-left py-2.5 px-3 text-xs font-bold text-foreground-600 whitespace-nowrap">날짜</th>
                          <th className="text-left py-2.5 px-3 text-xs font-bold text-foreground-600 whitespace-nowrap">구분</th>
                          <th className="text-left py-2.5 px-3 text-xs font-bold text-foreground-600 whitespace-nowrap">내용</th>
                          <th className="text-right py-2.5 px-3 text-xs font-bold text-foreground-600 whitespace-nowrap">포인트</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((item) => (
                          <tr key={item.id} className="border-b border-background-300/50 hover:bg-background-200/50 transition-colors">
                            <td className="py-2.5 px-3 text-xs text-foreground-600 whitespace-nowrap">
                              {new Date(item.created_at).toLocaleDateString("ko-KR")}
                            </td>
                            <td className="py-2.5 px-3">
                              <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded whitespace-nowrap ${
                                item.type === "적립" ? "bg-primary-500/10 text-primary-500" : "bg-secondary-500/10 text-secondary-500"
                              }`}>
                                {item.type}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-xs text-foreground-700">{item.detail}</td>
                            <td className={`py-2.5 px-3 text-xs font-bold text-right whitespace-nowrap ${item.amount > 0 ? "text-primary-500" : "text-secondary-500"}`}>
                              {item.amount > 0 ? `+${item.amount.toLocaleString()} P` : `${item.amount.toLocaleString()} P`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      <Footer />

      {/* 교환 확인 모달 (point 페이지에서도 접근 가능하도록 바깥에 배치) */}
      {/* 이미 gift 탭에 모달이 있으므로 point 탭에서는 모달이 필요 없음 */}
    </div>
  );
}